import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { Button, Message } from '@meitu/saas-ui';
import { rotatePoint } from '@meitu/saas-editor-core/es/utils/math-kit';
import { traverseTree } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { uploadBase64ToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { getDefaultClientBackgroundValue } from '@meitu/saas-editor-core/es/version-parse';
import {
    IBackgroundLayerProps,
    ILayerProps,
    LayerType,
    MaterialIdsType,
} from '@meitu/saas-editor-core/es/version-types';
import { isEditTagMaterial } from '@/hooks/fetch-template/editor-mode';
import { parseClientToServerLayers } from '@/hooks/save-template/client-to-server';
import { editor, type IEditor } from '@/instance';
import { commitTag } from '@/services/tag';
import { getTemplateDpi } from '@/services/template';
import { useTemplateModel } from '@/store/models/template-model';
import { getQueryParam } from '@/utils/query';
import { TagMaterialType } from './constant';

const moveLayerTo00 = (layer: ILayerProps) => {
    const left = layer.left;
    const top = layer.top;
    traverseTree(layer, (child: ILayerProps) => {
        child.left = child.left - left;
        child.top = child.top - top;
    });

    return layer;
};

const rotateLayerTo0 = (layer: ILayerProps) => {
    const { rotate } = layer;
    const centerPoint = { x: layer.width / 2, y: layer.height / 2 };
    traverseTree(layer, (child: ILayerProps) => {
        child.rotate = child.rotate - rotate;
        const point = {
            x: child.left + child.width / 2 - centerPoint.x,
            y: -(child.top + child.height / 2 - centerPoint.y),
        };
        const newPoint = rotatePoint(point, rotate);
        child.left = newPoint.x + centerPoint.x - child.width / 2;
        child.top = -newPoint.y + centerPoint.y - child.height / 2;
    });

    return layer;
};

const hasTextLayer = (layer: ILayerProps) => {
    let hasTextLayer = false;
    traverseTree(layer, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            hasTextLayer = true;
        }
    });
    return hasTextLayer;
};

const getServerLayer = async (parentLayer: ILayerProps, editor: IEditor) => {
    const activeStageIndex = editor.services.frame.getActiveIndex();
    const { width: stageWidth, height: stageHeight } = editor.services.frame.getSizeByIndex(activeStageIndex);
    const unit = editor.services.editor.getUnit();

    const stages = await editor.download.getResult(false);
    let layer: ILayerProps | null = null;
    for (let i = 0; i < stages.length; i++) {
        for (let j = 0; j < stages[i].layers.length; j++) {
            if (stages[i].layers[j].id === parentLayer.id.split('__')[0]) {
                layer = cloneDeep(stages[i].layers[j]);
                break;
            }
        }
    }

    const clientDataResultStages = cloneDeep(editor.services.frame.getFrames()).map((item) => {
        return {
            width: item[0].width,
            height: item[0].height,
            layers: item,
        };
    });

    let clientLayer: ILayerProps | null = null;

    for (let i = 0; i < clientDataResultStages.length; i++) {
        for (let j = 0; j < clientDataResultStages[i].layers.length; j++) {
            if (clientDataResultStages[i].layers[j].id === parentLayer.id) {
                clientLayer = cloneDeep(clientDataResultStages[i].layers[j]);
                break;
            }
        }
    }

    if (!layer || !clientLayer) {
        return null;
    }

    const originLeft = layer.left;
    const originTop = layer.top;
    layer = rotateLayerTo0(moveLayerTo00(cloneDeep(layer)));
    const defaultBg = getDefaultClientBackgroundValue(layer.width, layer.height);
    (defaultBg.data as IBackgroundLayerProps).backgroundColor = '#00000000';

    const resultCanvas = await editor.download.drawLayers(
        [defaultBg, ...editor.download.getDrawLayers([rotateLayerTo0(moveLayerTo00(cloneDeep(clientLayer)))])],
        'png',
        false,
    );

    const canvas = document.createElement('canvas');
    canvas.width = layer.width;
    canvas.height = layer.height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(resultCanvas, layer.left, layer.top, layer.width, layer.height, 0, 0, layer.width, layer.height);
    const mineType = 'image/png';
    const { url: preview } = await uploadBase64ToObs(canvas.toDataURL(mineType, 0.8));
    // 防止版本太高 生成的花字素材在pc版本太高了
    traverseTree(layer, (child: ILayerProps) => {
        if (child.extraInfo && child.extraInfo.modified && Object.keys(child.extraInfo).length === 1) {
            child.extraInfo = undefined;
        }
    });

    const { layers: serviceLayers, version } = parseClientToServerLayers([layer], false, {
        unit,
        resolution: getTemplateDpi(),
    });

    return {
        layer: serviceLayers[0],
        width: layer.width,
        height: layer.height,
        preview,
        version,
        stageWidth,
        stageHeight,
        originLeft,
        originTop,
    };
};

const PickUpMaterialBtn = () => {
    const templateId = useTemplateModel((state) => state.templateId);
    const mcpSourceId = useTemplateModel((state) => state.mcpSourceId);
    const tagInfoId = useTemplateModel((state) => state.tagInfoId);
    const tagId = getQueryParam('tag_id');
    const materialId = getQueryParam('material_id');

    const [pickMaterialTitle, setPickMaterialTitle] = useState<string | null>(
        editor.services.frame.getActivePickLayer()?.id || null,
    );
    useEffect(() => {
        editor.services.frame.onActiveLayerDataUpdate((layer, pickLayer) => {
            const id = pickLayer?.id || layer?.id || null;
            if (!id) {
                setPickMaterialTitle('提取背景');
            } else {
                const layer = editor.services.frame.getActiveLayer();
                if (layer?.layerType === LayerType.IMAGE_LAYER || layer?.layerType === LayerType.VECTOR_LAYER) {
                    setPickMaterialTitle('提取图片');
                } else if (layer?.layerType === LayerType.TEXT_LAYER) {
                    setPickMaterialTitle('提取图文');
                } else if (layer?.layerType === LayerType.GROUP_LAYER) {
                    let hasTextLayer = false;
                    traverseTree(layer, (child: ILayerProps) => {
                        if (child.layerType === LayerType.TEXT_LAYER) {
                            hasTextLayer = true;
                        }
                    });
                    if (hasTextLayer) {
                        setPickMaterialTitle('提取图文');
                    } else {
                        setPickMaterialTitle('提取图片');
                    }
                }
            }
        });
    }, []);

    if (!((templateId && parseInt(templateId) > 0) || isEditTagMaterial())) {
        return null;
    }

    const handlePickUpMaterial = async () => {
        let layer = null as ILayerProps | null;
        if (!isEditTagMaterial()) {
            const activeLayer = editor.services.frame.getActiveLayer();
            const bgLayer = editor.services.frame.getBackgroundLayer(editor.services.frame.getActiveFrameId());
            if (!activeLayer && !(bgLayer?.data as IBackgroundLayerProps).url) {
                Message.error('当前没有选中图层');
                return;
            }

            layer = activeLayer ? activeLayer : bgLayer;
        } else {
            layer = editor.services.frame.getFrames()[0][1] || null;
        }

        if (!layer) {
            Message.error('无法提取素材 请确认有图层');
            return;
        }

        const material_id = templateId ? parseInt(templateId) : undefined;
        const layer_id = layer.id;
        const material_type = hasTextLayer(layer)
            ? TagMaterialType.pic_text
            : layer.layerType === LayerType.BACKGROUND_LAYER
              ? TagMaterialType.pic_bg
              : TagMaterialType.pic_static_sticker;
        const is_sync = 1;
        const tag_category_id = 1;
        const source_id = mcpSourceId ? parseInt(mcpSourceId) : undefined;
        const layer_tag_info = [] as any[];
        const id = tagInfoId ? parseInt(tagInfoId) : materialId && tagId ? parseInt(tagId) : undefined;

        const serverLayer = await getServerLayer(layer, editor);

        if (!serverLayer) {
            Message.error('提取素材失败');
            return;
        }
        const req = await commitTag({
            material_id,
            layer_id,
            layer_formula: JSON.stringify(serverLayer),
            preview: serverLayer.preview,
            version: serverLayer.version,
            material_type,
            is_sync,
            tag_category_id,
            id,
            layer_tag_info,
            source_id,
        });

        if (req.data?.data?.material_id) {
            const material_id = req.data?.data?.material_id;

            layer.materialIds = layer.materialIds || {};
            if (material_type === TagMaterialType.pic_text) {
                layer.materialIds[MaterialIdsType.TEXT_MATERIAL] = [material_id.toString()];
            } else {
                layer.materialIds[MaterialIdsType.IMAGE_MATERIAL] = [material_id.toString()];
            }

            editor.services.frame.addOrUpdateLayer(layer);
        }

        Message.success('提取素材成功');
    };

    return (
        <>
            <Button type="primary" data-blue onClick={() => handlePickUpMaterial()} style={{ marginRight: '10px' }}>
                {isEditTagMaterial() ? '保存素材' : pickMaterialTitle}
            </Button>
        </>
    );
};

export default PickUpMaterialBtn;
